
import Vue, { PropOptions } from 'vue';
import _ from 'lodash';
import moment from 'moment';
import { numFormat } from '@/helpers/numFormat';
import { MonthlyTarget } from '@/models/page-models/page-items/MonthlyTarget';

export default Vue.extend({
  props: {
    input: { type: Object, default: () => {} }  as PropOptions<MonthlyTarget>,
    data: { type: Object, default: () => {} },
    getVariable: { type: Function, default: () => () => {} },
    callFunction: { type: Function, default: () => () => {} },
    onChange: { type: Function, default: () => {} },
    parent: { type: Object, default: () => {} },
    rowDisabled: { type: Boolean, default: false }
  },
  data () {
    return { productLineExpanded: '', customerExpanded: '' };
  },
  computed: {
    monthlyTargetType: function() {
      return this.input.targetType;
    },
    monthlyTargetData: function(): any {
      return this.getVariable(this.input.monthlyTargetDataVariable);
    },
    showCalloffVersion: function(): boolean {
      if(this.input.formVariable) {
        const { year, month } = this.getVariable(this.input.formVariable);
        const fullDate = moment(`${year} ${month}`, 'YYYY MMMM');
        const cutoffDate = moment('2025-10-01', 'YYYY-MM-DD');
        return fullDate.isBefore(cutoffDate) ? false : true;
      }
      return false;
    },
    weightedAverageKey: function(): string {
      if(this.monthlyTargetType == 'Value') {
        return this.showCalloffVersion ? 'totalTargetValue' : 'targetSAR';
      } else {
        return 'targetQuantity';
      }
    },
    tableHeaders: function(): { text: string, value: string, align?: string }[] {
      const tableHeaders: { text: string, value: string }[] = [
        { text: 'Product Line', value: 'productLine' },
        { text: 'Customer', value: 'customer' },
        this.showCalloffVersion ? { text: 'Product #', value: 'productNo' } : { text: 'Order #', value: 'orderNo' },
        { text: 'Item Alias', value: 'product' },
        { text: 'Base Unit', value: 'baseUnit' }
      ];
      if(this.monthlyTargetType == 'Quantity') {
        tableHeaders.push(...[
          { text: 'Target', value: 'targetQuantity' },
          { text: '% Completion', value: 'completion' },
          { text: 'Balance', value: 'balanceQty' },
          { text: 'Invoiced', value: 'invoiceSum' },
          { text: 'Hold', value: 'hold' }
        ]);
      }
      if(this.monthlyTargetType == 'Value') {
        tableHeaders.push(...[
          { text: 'Target', value: (this.showCalloffVersion ? 'totalTargetValue': 'targetSAR') },
          { text: '% Completion', value: 'completion' },
          { text: 'Balance', value: 'balanceSR' },
          { text: 'Invoiced', value: 'invoiceSum' },
          { text: 'Hold', value: 'hold' }
        ]);
      }
      const alignedTableHeaders: { text: string, value: string, align?: string }[] = tableHeaders.map((tableHeader: { text: string, value: string }) =>  {
        return { ...tableHeader, align: 'center' };
      });
      alignedTableHeaders.push({ text: '', value: 'data-expand' });
      return alignedTableHeaders;
    },
    groupedTargetData: function(): any[] {
      const sortOrder = [ "Wet Wipes", "PSL", "Ribbon", "Baking Cup", "Tulip Cup", "Baking Mold", "Wrap Paper", "Cake Board", "Corrugated Paper", "Tray Cup", "Doilies", "Cup Cover", "Coaster", "Cake Box", "Tray", "Coffee Filter" ];
      const productLineGroupedTargetData = _.map(this.monthlyTargetData, ((productLineCustomerData: any, productLine: string ) => {

        const productLineCustomerDataArray = _.map(productLineCustomerData, ((customerOrderDataArray: any[], customer: string) => {
          const targetSAR = customerOrderDataArray.reduce((targetSAR, orderData) => targetSAR + (orderData.targetSAR || 0), 0);
          const totalTargetValue = customerOrderDataArray.reduce((totalTargetValue, orderData) => totalTargetValue + (orderData.totalTargetValue || 0), 0);
          const targetQuantity = customerOrderDataArray.reduce((targetQuantity, orderData) => targetQuantity + (orderData.targetQuantity || 0), 0);
          const balanceSR = customerOrderDataArray.reduce((balanceSR, orderData) => balanceSR + (orderData.balanceSR || 0), 0);
          const balanceQty = customerOrderDataArray.reduce((balanceQty, orderData) => balanceQty + (orderData.balanceQty || 0), 0);
          const balancePercentage = 
            customerOrderDataArray.reduce((balancePercentage, orderData) => balancePercentage + orderData.balancePercentage, 0) / customerOrderDataArray.length;
          const completion =  this.weightedAverage(customerOrderDataArray, this.weightedAverageKey);
          const hold = customerOrderDataArray.reduce((hold, orderData) => hold + (orderData.hold || 0), 0);
          const invoiceSum = customerOrderDataArray.reduce((invoiceSum, orderData) => invoiceSum + (orderData.invoiceSum || 0), 0);
          return {
            productLine,
            customer,
            orderNo: '-',
            productNo: '-',
            baseUnit: this.monthlyTargetType == 'Value' ? 'SAR' : '',
            product: '-',
            targetSAR,
            totalTargetValue,
            targetQuantity,
            balanceSR,
            balanceQty,
            balancePercentage,
            completion,
            hold,
            invoiceSum,
            customerOrderDataArray,
            expandable: 'customer'
          };
        }));

        const targetSAR = productLineCustomerDataArray.reduce((targetSAR, orderData) => targetSAR + orderData.targetSAR, 0);
        const totalTargetValue = productLineCustomerDataArray.reduce((totalTargetValue, orderData) => totalTargetValue + orderData.totalTargetValue, 0);
        const targetQuantity = productLineCustomerDataArray.reduce((targetQuantity, orderData) => targetQuantity + orderData.targetQuantity, 0);
        const balanceSR = productLineCustomerDataArray.reduce((balanceSR, orderData) => balanceSR + orderData.balanceSR, 0);
        const balanceQty = productLineCustomerDataArray.reduce((balanceQty, orderData) => balanceQty + orderData.balanceQty, 0);
        const balancePercentage = 
          productLineCustomerDataArray.reduce((balancePercentage, orderData) => balancePercentage + orderData.balancePercentage, 0) / productLineCustomerDataArray.length;
        const completion =  this.weightedAverage(productLineCustomerDataArray, this.weightedAverageKey);
        const hold = productLineCustomerDataArray.reduce((hold, orderData) => hold + orderData.hold, 0);
        const invoiceSum = productLineCustomerDataArray.reduce((invoiceSum, orderData) => invoiceSum + orderData.invoiceSum, 0);
        return {
          productLine,
          customer: 'All',
          orderNo: '-',
          productNo: '-',
          baseUnit: this.monthlyTargetType == 'Value' ? 'SAR' : '',
          product: '-',
          targetSAR,
          totalTargetValue,
          targetQuantity,
          balanceSR,
          balanceQty,
          balancePercentage,
          completion,
          hold,
          invoiceSum,
          productLineCustomerDataArray,
          expandable: 'productLine'
        };
      }));
      return productLineGroupedTargetData.sort((groupA: any, groupB: any) => sortOrder.indexOf(groupA.productLine) - sortOrder.indexOf(groupB.productLine));
    },
    formattedTargetData: function(): any[] {
      const targetSAR = this.groupedTargetData.reduce((targetSAR, orderData) => targetSAR + orderData.targetSAR, 0);
      const totalTargetValue = this.groupedTargetData.reduce((totalTargetValue, orderData) => totalTargetValue + orderData.totalTargetValue, 0);
      const targetQuantity = this.groupedTargetData.reduce((targetQuantity, orderData) => targetQuantity + orderData.targetQuantity, 0);
      const balanceSR = this.groupedTargetData.reduce((balanceSR, orderData) => balanceSR + orderData.balanceSR, 0);
      const balanceQty = this.groupedTargetData.reduce((balanceQty, orderData) => balanceQty + orderData.balanceQty, 0);
      const balancePercentage = this.groupedTargetData.reduce((balancePercentage, orderData) => balancePercentage + orderData.balancePercentage, 0) / this.groupedTargetData.length;
      const completion =  this.weightedAverage(this.groupedTargetData, this.weightedAverageKey);
      const hold = this.groupedTargetData.reduce((hold, orderData) => hold + orderData.hold, 0);
      const invoiceSum = this.groupedTargetData.reduce((invoiceSum, orderData) => invoiceSum + orderData.invoiceSum, 0);
      const targetData: any[] = [{
        productLine: 'Total Target',
        customer: 'All',
        orderNo: '-',
        productNo: '-',
        baseUnit: this.monthlyTargetType == 'Value' ? 'SAR' : '',
        product: '-',
        targetSAR,
        totalTargetValue,
        targetQuantity,
        balanceSR,
        balanceQty,
        balancePercentage,
        completion,
        hold,
        invoiceSum,
      }];
      this.groupedTargetData.forEach((groupData: any) => {
        const { productLineCustomerDataArray, ...groupedTargetData } = groupData;
        targetData.push(groupedTargetData);
        if(groupedTargetData.productLine == this.productLineExpanded) {
          const matchedCustomerIndex = _.findIndex(productLineCustomerDataArray, (groupedCustomer: any) => groupedCustomer.customer == this.customerExpanded);
          if(matchedCustomerIndex == -1) {
            targetData.push(...productLineCustomerDataArray);
          } else {
            targetData.push(..._.slice(productLineCustomerDataArray, 0, matchedCustomerIndex + 1 ));
            targetData.push(...productLineCustomerDataArray[matchedCustomerIndex].customerOrderDataArray);
            targetData.push(..._.slice(productLineCustomerDataArray, matchedCustomerIndex + 1 ));
          }
        }
      });
      return targetData;
    }
  },
  mounted() {
  },
  methods: {
    expand(expandableType: 'productLine' | 'customer', productLine: string, customer: string) {
      if(expandableType == 'productLine') {
        this.productLineExpanded = productLine;
      } else {
        this.customerExpanded = customer;
      }
    },
    collapse(expandableType: 'productLine' | 'customer') {
      if(expandableType == 'productLine') {
        this.productLineExpanded = '';
      }
      this.customerExpanded = '';
    },
    formatNumber(numValue: number): string | number {
      return numFormat(numValue);
    },
    weightedAverage(orderArray: any[], weightKey: string, valueKey: string = 'completion'): number {
      const weightedSumProduct = orderArray.reduce((sum, orderData) => sum + _.get(orderData, weightKey, 0) * _.get(orderData, valueKey, 0), 0);
      const weightedSum = orderArray.reduce((weight, orderData) => weight + _.get(orderData, weightKey, 0), 0);
      return weightedSum == 0 ? 0 : weightedSumProduct / weightedSum;
    }
  }
});
